import { useEffect, useState } from "react";
import { Box, Link } from "@mui/material";
import { UILabel } from "./UILabel";
import { UIHover } from "./UIHover";
import { UIStandardLayer } from "./UIStandardLayer";
import { UIButton } from "./UIButton";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { IBuildingBlockBaseControl } from "./BuildingBlockInterfaces";

export interface IUIObjectList<T extends object> extends IBuildingBlockBaseControl {
    values: T[];
    renderItemUI: (item: T) => JSX.Element;
    getItemName: (item: T, allItems: T[]) => string;
    createNewItem: () => T;
    onChange?: (items: T[]) => void;
    noDataLabel?: string;
}

// this editor is to provide a list an a sub ui
export function UIObjectList<T extends object>(props: IUIObjectList<T>) {
    const [editing, setEditing] = useState<T | undefined>(undefined);
    const [val, setVal] = useState<T[]>(props.values);

    useEffect(() => {
        setVal(props.values);
    }, [props.values]);

    const editItem = (item: T) => {
        setEditing(item);
    };

    const addNew = () => {
        let newObj = props.createNewItem();
        editItem(newObj);
    };

    const onEditComplete = (item: T) => {
        let newVal = val;
        if (val.indexOf(item) === -1) newVal.push(item);

        setVal(newVal);

        if (props.onChange) props.onChange(newVal);
    };

    return (
        <Box
            display={"flex"}
            flexDirection={"column"}
            sx={{ width: "100%", ...props.sx }}
        >
            {props.label && <UILabel paddingBottom={"5px"}>{props.label}</UILabel>}
            <Box
                marginBottom={"3px"}
                padding={"4px"}
                border={"1px solid " + (props.error ? UIStyleConstants.RED_COLOR : UIStyleConstants.GRAY_LIGHTER_COLOR)}
                borderRadius="4pxpx"
                minHeight={"35px"}
            >
                {(!val || val.length === 0) && (
                    <UILabel paddingLeft={"8px"}>{props.noDataLabel || "Click Add"}</UILabel>
                )}
                {val.map((item) => (
                    <Box
                        display={"flex"}
                        flexDirection={"column"}
                    >
                        <UIHover
                            renderControl={(h) => {
                                return (
                                    <>
                                        <UILabel paddingLeft={"8px"}>{props.getItemName(item, val)}</UILabel>
                                        {h && (
                                            <Box
                                                display={"flex"}
                                                flexDirection={"row"}
                                                justifyContent={"end"}
                                            >
                                                {val.indexOf(item) !== 0 && (
                                                    <Link
                                                        onClick={() => {
                                                            let index = val.indexOf(item);
                                                            let newVal = DataUtilities.moveUpInArray<T>(val, index);
                                                            setVal(newVal);
                                                            if (props.onChange) props.onChange(newVal);
                                                        }}
                                                        paddingRight={"10px"}
                                                    >
                                                        Move Up
                                                    </Link>
                                                )}
                                                {val.indexOf(item) !== val.length - 1 && (
                                                    <Link
                                                        onClick={() => {
                                                            let index = val.indexOf(item);
                                                            let newVal = DataUtilities.moveDownInArray<T>(val, index);
                                                            setVal(newVal);
                                                            if (props.onChange) props.onChange(newVal);
                                                        }}
                                                        paddingRight={"10px"}
                                                    >
                                                        Move Down
                                                    </Link>
                                                )}
                                                <Link
                                                    onClick={() => {
                                                        let index = val.indexOf(item);

                                                        let newVal = [...val];
                                                        newVal.splice(index, 1);
                                                        setVal(newVal);
                                                        if (props.onChange) props.onChange(newVal);
                                                    }}
                                                    paddingRight={"10px"}
                                                >
                                                    Delete
                                                </Link>
                                                <Link onClick={() => editItem(item)}>Edit</Link>
                                            </Box>
                                        )}
                                    </>
                                );
                            }}
                        />
                    </Box>
                ))}
            </Box>
            <UILabel
                color={props.error ? UIStyleConstants.RED_COLOR : undefined}
                variant={"hint"}
            >
                {props.error ?? props.hint}
            </UILabel>
            <Box
                display={"flex"}
                flexDirection={"row"}
                width={"100%"}
                justifyContent={"end"}
            >
                <UIButton onClick={addNew}>Add</UIButton>
            </Box>
            {editing && (
                <UIStandardLayer
                    title={"Edit " + (props.label || "")}
                    primaryActionText={"Done"}
                    onPrimaryAction={() => {
                        setEditing(undefined);
                        onEditComplete(editing);
                    }}
                    secondaryActionText={val.indexOf(editing) === -1 ? "Cancel" : undefined}
                    onSecondaryAction={() => {
                        setEditing(undefined);
                    }}
                    onClose={() => setEditing(undefined)}
                    open={editing !== undefined}
                    renderUI={() => {
                        return props.renderItemUI(editing);
                    }}
                ></UIStandardLayer>
            )}
        </Box>
    );
}
